import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Search } from 'lucide-react';
import { useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import CityTable from '../components/admin/CityTable';
import AddCityModal from '../components/admin/modals/AddCityModal';

const SEARCH_DEBOUNCE_MS = 350;

const CitiesList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);

  const { cities, fetchCities, fetchMoreCities, hasMore } = useAdminStore();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    fetchCities({ offsetCount: 0, search: debouncedSearch })
      .catch(() => {
        toast.error(t('generic_error', { ns: 'common' }));
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, fetchCities, t]);

  const handleAddClick = () => {
    setOpenAddModal(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await fetchMoreCities();
    } catch {
      toast.error(t('generic_error', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('breadcrumbs.cities', { ns: 'common', defaultValue: 'Cities' })}
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <div className="flex flex-col gap-3 border-b border-gray-300 bg-gray-50 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <label className="relative flex min-w-0 flex-1 sm:max-w-md">
            <span className="sr-only">{t('search_cities', { ns: 'admin' })}</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={t('search_cities_placeholder', { ns: 'admin' })}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
              autoComplete="off"
            />
          </label>

          <Button
            type="button"
            variant="transparent-with-border"
            className="w-full shrink-0 transition duration-300 ease-in-out hover:bg-gray-200 sm:ml-auto sm:w-auto md:px-8"
            onClick={handleAddClick}
          >
            {t('add_new_city', { ns: 'common' })}
          </Button>
        </div>

        <div>
          <CityTable cities={cities} />
        </div>

        {!loading && cities.length === 0 && (
          <p className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {debouncedSearch
              ? t('cities_no_search_results', { ns: 'admin' })
              : t('cities_empty', { ns: 'admin' })}
          </p>
        )}

        {hasMore?.cities && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
                : t('loadMore', { ns: 'common', defaultValue: 'Load more' })}
            </Button>
          </div>
        )}

        {openAddModal && <AddCityModal onClose={() => setOpenAddModal(false)} />}
      </div>
    </>
  );
};

export default CitiesList;
